import { Anchor as PopoverAnchor } from "@radix-ui/react-popover";
import { useCallback, useEffect, useState, type FC } from "react";

import { IconCircleX } from "@/assets/icons/central/IconCircleX";
import { IconChevronDownSmall } from "@/assets/icons/geist/IconChevronDownSmall";
import { Button } from "@/components/flexkit/Button";
import { useToast } from "@/components/flexkit/toast";
import { Input } from "@/components/podkit/forms/Input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/podkit/popover/Popover";
import { appMainService } from "@/ipc";
import { formatError } from "@/utils/errors";
import { isValidDomainInput, normalizeDomain } from "@/utils/url-validation";

export type DesktopSigninViewProps = {
    /** The bare domain displayed when a custom domain is active (e.g. "acme.ona.dev") */
    customDomain: string;
    /** Whether a custom domain is active */
    isCustomDomain: boolean;
    /** Current value of the domain input field */
    serverDomain: string;
    /** Whether a save/reset operation is in progress */
    isSaving: boolean;
    onSignin: () => void;
    onDomainChange: (value: string) => void;
    /** Called to connect with the current serverDomain. */
    onSave: () => void;
    /** Called to clear the custom domain and reset to default. */
    onReset: () => void;
};

/** Presentational component — all state is controlled via props. */
export const DesktopSigninView: FC<DesktopSigninViewProps> = ({
    customDomain,
    isCustomDomain,
    serverDomain,
    isSaving,
    onSignin,
    onDomainChange,
    onSave,
    onReset,
}) => {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    const handleCancel = useCallback(() => {
        setIsPopoverOpen(false);
    }, []);

    const handleSave = useCallback(() => {
        onSave();
        setIsPopoverOpen(false);
    }, [onSave]);

    return (
        <div id="desktop-signin-container" className="flex flex-col gap-2">
            <div className="flex justify-center" onClick={onSignin} data-tracking-id="connect-to-ona-desktop-signin">
                <Button id="signin" variant={"primary"} size={"lg"}>
                    Sign into Ona
                </Button>
            </div>
            <div className="flex items-center justify-center gap-1">
                <Popover
                    open={isPopoverOpen}
                    onOpenChange={(open) => {
                        if (open) {
                            setIsPopoverOpen(true);
                        } else {
                            handleCancel();
                        }
                    }}
                >
                    <PopoverAnchor asChild>
                        <div className="flex items-center gap-0.5">
                            {isCustomDomain ? (
                                <>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            className="text-sm text-content-muted"
                                            disabled={isSaving}
                                            data-tracking-id="edit-custom-domain"
                                        >
                                            <span>Using {customDomain}</span>
                                        </Button>
                                    </PopoverTrigger>
                                    <button
                                        className="inline-flex items-center justify-center rounded-full p-0.5 text-content-muted transition-colors hover:text-content-primary"
                                        onClick={onReset}
                                        disabled={isSaving}
                                        title="Clear custom domain"
                                        data-tracking-id="clear-custom-domain"
                                    >
                                        <IconCircleX className="size-4" />
                                    </button>
                                </>
                            ) : (
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        className="text-sm text-content-muted"
                                        data-tracking-id="edit-custom-domain"
                                    >
                                        <span>Use custom domain</span>
                                        <IconChevronDownSmall
                                            size="sm"
                                            className="transition-transform group-hover:translate-y-0.5"
                                        />
                                    </Button>
                                </PopoverTrigger>
                            )}
                        </div>
                    </PopoverAnchor>
                    <PopoverContent align="center" className="w-72 p-3">
                        <div className="flex flex-col gap-3">
                            <Input
                                type="text"
                                value={serverDomain}
                                onChange={(e) => onDomainChange(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && serverDomain.trim()) {
                                        handleSave();
                                    }
                                    if (e.key === "Escape") {
                                        handleCancel();
                                    }
                                }}
                                disabled={isSaving}
                                autoFocus
                            />
                            <div className="flex justify-end gap-2">
                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={handleCancel}
                                    disabled={isSaving}
                                    data-tracking-id="cancel-custom-domain"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={handleSave}
                                    disabled={isSaving || !serverDomain.trim()}
                                    loading={isSaving}
                                    data-tracking-id="save-custom-domain"
                                >
                                    Connect
                                </Button>
                            </div>
                        </div>
                    </PopoverContent>
                </Popover>
            </div>
        </div>
    );
};

/** Wired-up version that talks to the desktop app via IPC. */
export const DesktopSignin: FC = () => {
    const { toast } = useToast();
    const cpHost = window.location.origin;
    const cpDomain = window.location.host;

    const [serverDomain, setServerDomain] = useState("");
    const [isCustomDomain, setIsCustomDomain] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Load current custom domain config on mount
    useEffect(() => {
        const loadConfig = async () => {
            if (!appMainService) return;
            try {
                const config = await appMainService.getCustomDomainConfig();
                setServerDomain(config.customDomain || "");
                setIsCustomDomain(!!config.customDomain);
            } catch (error) {
                console.error("Failed to load custom domain config:", error);
            }
        };
        void loadConfig();
    }, []);

    const handleConnectToGitpod = useCallback(async () => {
        if (!appMainService) return;
        const result = await appMainService.signin({ host: cpHost });
        if (result === "timeout") {
            console.error("[DesktopSignin] signin timed out");
            toast({ title: "Timed out", description: "Please try again" });
            return;
        }
        const parsed = parseSigninResult(result);
        if ("error" in parsed) {
            console.error("[DesktopSignin] failed to parse signin result:", parsed.error);
            toast({
                title: "Sign-in failed",
                description: "Unexpected response from desktop app. Check the console for details.",
            });
            return;
        }
        try {
            // clear cookies first
            await fetch("/auth/oidc/logout", {
                method: "POST",
                credentials: "same-origin",
            });
        } catch {
            // explicitly ignore as we're about to login using the desktop flow
        }
        try {
            const response = await fetch("/auth/desktop/callback", {
                method: "POST",
                credentials: "same-origin",
                body: new URLSearchParams(parsed),
            });
            if (!response.ok) {
                throw new Error(`${response.status} ${response.statusText}`);
            }
            window.location.href = "/";
        } catch (error) {
            console.error("[DesktopSignin] desktop callback failed:", error);
            toast({ title: "Sign-in failed", description: formatError(error) });
        }
    }, [cpHost, toast]);

    const handleDomainChange = useCallback((value: string) => {
        const trimmed = value.trim();
        // If the input looks like a URL, extract just the host (strips path, query, etc.)
        if (/^https?:\/\//.test(trimmed)) {
            try {
                setServerDomain(new URL(trimmed).host);
                return;
            } catch {
                // fall through to normalizeDomain
            }
        }
        setServerDomain(normalizeDomain(value));
    }, []);

    const handleSaveServer = useCallback(async () => {
        if (!appMainService) return;

        const trimmed = serverDomain.trim();

        if (trimmed && !isValidDomainInput(trimmed, import.meta.env.DEV)) {
            console.warn("[DesktopSignin] invalid domain input:", trimmed);
            toast({
                title: "Invalid domain",
                description: "Please enter a valid domain (e.g., custom.example.com).",
            });
            return;
        }

        setIsSaving(true);
        try {
            // setCustomDomain validates, persists, and seamlessly switches
            // the window to the new domain (no restart needed).
            const result = await appMainService.setCustomDomain({ domain: trimmed || undefined });
            if (!result.success) {
                console.error("[DesktopSignin] setCustomDomain failed:", result.error);
                setIsSaving(false);
                toast({
                    title: "Failed to connect",
                    description: result.error || "Could not connect to the domain. Please try again.",
                });
                return;
            }
            // Window will be recreated by the main process — no need to update state
        } catch (error) {
            console.error("[DesktopSignin] setCustomDomain failed:", error);
            setIsSaving(false);
            toast({ title: "Failed to save", description: formatError(error) });
        }
    }, [serverDomain, toast]);

    const handleResetToDefault = useCallback(async () => {
        if (!appMainService) return;

        setIsSaving(true);
        try {
            const result = await appMainService.setCustomDomain({ domain: undefined });
            if (!result.success) {
                console.error("[DesktopSignin] setCustomDomain(undefined) returned success=false");
                setIsSaving(false);
                toast({ title: "Failed to reset", description: "Could not clear the domain. Please try again." });
                return;
            }
            // Window will be recreated by the main process
        } catch (error) {
            console.error("[DesktopSignin] reset to default failed:", error);
            setIsSaving(false);
            toast({ title: "Failed to reset", description: formatError(error) });
        }
    }, [toast]);

    return (
        <DesktopSigninView
            customDomain={cpDomain}
            isCustomDomain={isCustomDomain}
            serverDomain={serverDomain}
            isSaving={isSaving}
            onSignin={handleConnectToGitpod}
            onDomainChange={handleDomainChange}
            onSave={handleSaveServer}
            onReset={handleResetToDefault}
        />
    );
};

function parseSigninResult(result: string): { code: string; code_verifier: string } | { error: string } {
    let parsed: unknown;
    try {
        parsed = JSON.parse(result);
    } catch {
        return { error: "Invalid response from desktop app (not JSON)" };
    }
    if (typeof parsed === "object" && parsed !== null && "code" in parsed && "code_verifier" in parsed) {
        return {
            code: (parsed as { code: string }).code,
            code_verifier: (parsed as { code_verifier: string }).code_verifier,
        };
    }
    return { error: "Unexpected response format from desktop app (missing expected fields)" };
}
